import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useGetPaymentsQuery, useExportPaymentsMutation } from 'api/main';
import { userSelectors } from 'reducers';

import { useUserRoleMatchCategory, useDebounce, useMatchAtLeastOneRole } from 'utils/hooks';
import {
    ORDER_BY,
    RoleNames,
    paymentEnrichments,
    paymentMethods,
    paymentStatus,
    roleCategories,
    sortDirection,
    listingActionButtonStyle,
} from 'utils/constants';
import { parseSum } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import CreditDialog from 'containers/payments/credit-transfers/CreditDialog';
import PaymentFilters from 'containers/payments/components/PaymentFilters';
import TransferRow from 'containers/payments/credit-transfers/TransferRow';
import {
    CustomTablePagination,
    EmptyTableBody,
    SMSDialog,
    QuickSearchField,
    ActionButton,
    FiltersButton,
} from 'components';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TableFooter,
} from '@mui/material';

const commonTableHeads = [
    { id: 1, isSortable: false, width: 11, label: texts.NOM },
    { id: 2, isSortable: false, width: 10, label: texts.DATE },
    { id: 3, isSortable: false, width: 8, label: texts.TYPE },
    { id: 4, isSortable: false, width: 11, label: texts.FUND },
    { id: 5, isSortable: false, width: 7, label: texts.ORDER_NUMBER },
    { id: 6, isSortable: false, width: 12, label: texts.MONTANT_SOUSCRIT },
    { id: 7, isSortable: false, width: 8, label: texts.MONTANT_VIREMENT },
    { id: 8, isSortable: false, width: 8, label: texts.CREDITED_AMOUNT },
];

const transferTableHeads = [
    ...commonTableHeads,
    { id: 9, isSortable: false, width: 9, label: texts.SUM_REMAINING },
    { id: 10, isSortable: false, width: 12, label: texts.FIRST_TRANSFER_DATE },
    { id: 'actions', isSortable: false, width: 4, label: '' },
];

const historyTableHeads = [
    ...commonTableHeads,
    { id: 9, isSortable: false, width: 12, label: texts.TRANSFER_DATE },
    { id: 10, isSortable: false, width: 13, label: texts.SETTLED_BY },
];

const ITEMS_PER_PAGE = 10;

const TotalRow = ({ total, label, hasActions }) => (
    <TableRow>
        <TableCell colSpan={6} align="right" sx={{ borderBottom: 'none' }}>
            <strong>{label}</strong>
        </TableCell>
        <TableCell colSpan={hasActions ? 5 : 3} sx={{ borderBottom: 'none' }}>
            {parseSum(total)}
        </TableCell>
    </TableRow>
);

TotalRow.propTypes = {
    total: PropTypes.number,
    label: PropTypes.string,
    hasActions: PropTypes.bool,
};

const TransfersTable = props => {
    // @hasActions prop also determines if the table needs styled rows or not
    const { title, hasActions, listStatus } = props;

    const mounted = useRef(false);
    const currentSubscription = useSelector(userSelectors.getCurrentSubscription);
    const isServicer = useUserRoleMatchCategory(roleCategories.SERVICERS);
    // eslint-disable-next-line no-undef
    const userIsStandardServicer = useMatchAtLeastOneRole([RoleNames.SERVICER_STANDARD]);

    const isAdminCommercial = useMatchAtLeastOneRole([RoleNames.ADMIN_CIF, RoleNames.PSI]);

    const [filters, setFilters] = useState({
        orderBy: ORDER_BY.finishedSubscriptionAt,
        direction: sortDirection.ASC,
        enrichments: Object.values(paymentEnrichments),
        type: paymentMethods.CREDIT_TRANSFER,
        company: isAdminCommercial ? currentSubscription?.distributorCompany?.id : undefined,
        status: listStatus,
        itemsPerPage: ITEMS_PER_PAGE,
        page: 1,
    });
    const [smsOpen, setSmsOpen] = useState(false);
    const [creditOpen, setCreditOpen] = useState(false);
    const [receiver, setReceiver] = useState(null);
    const [processingPayment, setProcessingPayment] = useState(null);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [keyword, setKeyword] = useState('');
    const debouncedKeyword = useDebounce(keyword, 500);

    const {
        data: { payments: transfers = [], lastPage } = { payments: [], lastPage: 1 },
        isFetching: listLoader,
    } = useGetPaymentsQuery(filters);
    const [exportPayments, { isLoading: exportLoading }] = useExportPaymentsMutation();

    const totalValue = useMemo(
        () => transfers.reduce((acc, crt) => acc + crt[0].amount, 0),
        [transfers]
    );

    const tableHeaders = useMemo(
        () => (hasActions ? transferTableHeads : historyTableHeads),
        [hasActions]
    );

    const filteredTableHeads = useMemo(() => {
        if (isServicer)
            return tableHeaders.filter(th => (userIsStandardServicer ? th.id !== 'actions' : true));
        if (hasActions) {
            return tableHeaders.filter(th => th.id !== 'actions');
        }
        return tableHeaders;
    }, [isServicer, tableHeaders, hasActions, userIsStandardServicer]);

    const leftOutWidth = useMemo(
        () =>
            tableHeaders
                .filter(th => !filteredTableHeads.some(fth => fth.id === th.id))
                .reduce((acc, crt) => acc + crt.width, 0),
        [tableHeaders, filteredTableHeads]
    );
    const handleToggleFilters = useCallback(() => setFiltersOpen(prevState => !prevState), []);
    const handleApplyFilters = useCallback(
        customFilters => {
            setFilters(prevState => ({ ...prevState, ...customFilters, page: 1 }));
        },
        [setFilters]
    );

    const handlePageChange = useCallback(
        page => () => setFilters(prevState => ({ ...prevState, page })),
        []
    );

    const onSendSMS = useCallback(
        user => () => {
            setReceiver(user);
            setSmsOpen(true);
        },
        []
    );

    const onCredit = useCallback(payment => {
        setProcessingPayment(payment);
        setCreditOpen(true);
    }, []);

    const handleCreditClose = useCallback(() => {
        setCreditOpen(false);
        setProcessingPayment(null);
    }, []);

    const handlePaymentsExport = useCallback(() => {
        exportPayments(filters);
    }, [filters, exportPayments]);

    useEffect(() => {
        // reset page to 1 when selected keyword is changed
        setFilters(prevState => ({
            ...prevState,
            keyword: debouncedKeyword,
            page: 1,
        }));
    }, [debouncedKeyword]);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    return (
        <Box
            sx={theme => ({
                backgroundColor: theme.palette.grey[100],
                marginTop: 4,
                border: `1px solid ${theme.palette.grey[100]}`,
                borderRadius: '6px',
                padding: '10px 15px',
            })}
        >
            <SMSDialog open={smsOpen} handleClose={() => setSmsOpen(false)} receiver={receiver} />
            <CreditDialog
                open={creditOpen}
                onClose={handleCreditClose}
                filters={filters}
                data={processingPayment}
            />
            <Typography variant="h5" color="primary" gutterBottom>
                {title}
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between" padding={2}>
                <Box display="flex" alignItems="center" padding={2}>
                    <QuickSearchField
                        value={keyword}
                        onChange={e => setKeyword(e.target.value)}
                        handleClear={() => setKeyword('')}
                    />
                    <FiltersButton onClick={handleToggleFilters} />
                </Box>
                {(listStatus === paymentStatus.PENDING_CONFIRMATION ||
                    listStatus === paymentStatus.CONFIRMED) && (
                    <ActionButton
                        variant="outlined"
                        sx={listingActionButtonStyle}
                        disabled={exportLoading}
                        onClick={handlePaymentsExport}
                    >
                        {texts.EXPORTER_LISTE}
                    </ActionButton>
                )}
            </Box>
            <PaymentFilters
                open={filtersOpen}
                applyFilters={handleApplyFilters}
                loading={listLoader}
            />
            <TableContainer>
                <Table style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                        {filteredTableHeads.map(th => {
                            let extraWidth = th.id === 1 ? leftOutWidth : 0;
                            return <col key={th.label} width={`${th.width + extraWidth}%`} />;
                        })}
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            {filteredTableHeads.map(th => (
                                <TableCell key={th.id}>{th.label}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    {!transfers.length || listLoader ? (
                        <EmptyTableBody loading={listLoader} colSpan={filteredTableHeads.length} />
                    ) : (
                        <TableBody>
                            {transfers.map(t => (
                                <TransferRow
                                    key={t[0].id}
                                    transfer={t}
                                    onSendSMS={onSendSMS}
                                    onCredit={onCredit}
                                    hasActions={hasActions}
                                    isServicer={isServicer && !userIsStandardServicer}
                                />
                            ))}
                            <TotalRow
                                hasActions={hasActions && isServicer}
                                total={totalValue}
                                label={texts.TOTAL_TRANSFERS}
                            />
                        </TableBody>
                    )}
                    <TableFooter>
                        <TableRow>
                            <CustomTablePagination
                                currentPage={filters.page}
                                lastPage={lastPage}
                                changePage={handlePageChange}
                                columns={filteredTableHeads.length}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Box>
    );
};

TransfersTable.propTypes = {
    title: PropTypes.string.isRequired,
    hasActions: PropTypes.bool,
    listStatus: PropTypes.string,
};

export default TransfersTable;
